<template>
    <div class="position-relative">
        <div class="legend d-flex flex-wrap gap-3 mb-3">
            <div class="legend-item">
                <span class="legend-dot accent-normal"></span>Running
            </div>
            <div class="legend-item">
                <span class="legend-dot accent-today"></span>Checkout Today
            </div>
            <div class="legend-item">
                <span class="legend-dot accent-expired"></span>Checkout Overdue
            </div>
        </div>
        <div v-if="hasGuests" class="row g-3">
            <div
                v-for="guest in guests"
                :key="guest.id"
                class="col-12 col-sm-6 col-md-4 col-lg-3"
            >
                <div class="guest-card h-100" :class="checkoutAccent(guest.discharge_date_raw)" @click="onClickCard(guest)">
                    <div class="guest-card-body">
                        <div class="guest-head">
                            <div class="avatar">{{ initial(guest.guest_name) }}</div>
                            <div class="guest-head-text">
                                <div class="guest-name" :title="guest.guest_name">{{ guest.guest_name }}</div>
                                <div class="reservation-no">{{ guest.reservation_no }}</div>
                            </div>
                        </div>
                        <div class="rooms-line">
                            <i class="fa fa-bed me-2"></i>
                            <span class="rooms-text">{{ roomLabel(guest.rooms) }}</span>
                            <span v-if="roomCount(guest.rooms) > 1" class="room-count">{{ roomCount(guest.rooms) }} rooms</span>
                        </div>
                        <div class="dates-line">
                            <div class="date-item">
                                <i class="fa fa-sign-in-alt me-1"></i>{{ dateLabel(guest.check_in) }}
                            </div>
                            <div class="date-item">
                                <i class="fa fa-sign-out-alt me-1"></i>{{ dateLabel(guest.discharge_date) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!hasGuests && !loading" class="empty-state">
            No running guest found
        </div>

        <div class="mt-3">
            <Pagination :offset="offset" @onPageChange="onPageChange"/>
        </div>

        <PayModal
            v-if="showPayModal"
            :reservation="selectedGuest"
            :showDetails="true"
            @onClose="showPayModal = false"
            @onSubmit="onPaid"
            @onSeeDetails="onSeeDetails"
        />

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import Loader from '@/components/atom/LoaderComponent';
import Pagination from '@/components/atom/Pagination';
import PayModal from '@/components/molecule/company/hotel/hotel-reservation/PayModal.vue';
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import handleHotel from '@/services/modules/hotel/hotel';

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const { fetchHotelReservationList } = handleHotel();

const offset = ref(20);
const loading = ref(false);
const guests = ref([]);
const showPayModal = ref(false);
const selectedGuest = ref(null);

const hasGuests = computed(() => guests.value.length > 0);

const getQuery = () => {
    let query = `?company_id=${$route.params.companyId}`;
    query += '&reg_status=checked_in&settlement_status=not_settled';
    if ($route.query.page) query += '&page=' + $route.query.page;
    query += '&offset=' + offset.value;
    return query;
};

const setPagination = (data) => {
    if (!data.data.length) return;
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    });
};

const getRunningGuestList = async () => {
    loading.value = true;
    guests.value = [];
    await fetchHotelReservationList(getQuery()).then(res => {
        if (!res.status) {
            $store.commit('resetPagination');
            return;
        }
        guests.value = res.data.data;
        setPagination(res.data);
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const onPageChange = async (page) => {
    const routeQuery = Object.assign({}, $route.query, { page });
    await $router.push({ path: $route.path, query: routeQuery });
    await getRunningGuestList();
};

const roomLabel = (rooms) => {
    if (!rooms) return '—';
    return rooms;
};

const roomCount = (rooms) => {
    if (!rooms) return 0;
    return rooms.split(',').length;
};

const initial = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
};

const dateLabel = (date) => {
    if (!date) return '—';
    return date;
};

const checkoutAccent = (dischargeDateRaw) => {
    if (!dischargeDateRaw) return 'accent-normal';
    const today = new Date().toISOString().slice(0, 10);
    if (dischargeDateRaw < today) return 'accent-expired';
    if (dischargeDateRaw === today) return 'accent-today';
    return 'accent-normal';
};

const onClickCard = (guest) => {
    selectedGuest.value = guest;
    showPayModal.value = true;
};

const onPaid = () => {
    showPayModal.value = false;
    getRunningGuestList();
};

const onSeeDetails = (guest) => {
    $router.push({
        name: 'hotel-bill-statement',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            ipdRegisterId: guest.id,
            pageId: guest.id,
        },
    });
};

onMounted(() => {
    getRunningGuestList();
});
</script>

<style scoped>
.guest-card {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 160px;
    background: #ffffff;
    border: 1px solid #eceef1;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    transition: box-shadow 0.18s ease, transform 0.18s ease, border-color 0.18s ease;
    cursor: pointer;
}
.guest-card:hover {
    box-shadow: 0 8px 20px rgba(16, 24, 40, 0.12);
    transform: translateY(-2px);
    border-color: #dfe3e8;
}
.legend-item {
    display: flex;
    align-items: center;
    font-size: 0.82rem;
    color: #475467;
}
.legend-dot {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-right: 6px;
    border: 1px solid #dfe3e8;
}
.accent-normal {
    background: #eaf1fb;
    border-color: #cfe0f5;
}
.accent-today {
    background: #fdf1dc;
    border-color: #f5dfae;
}
.accent-expired {
    background: #fdeaea;
    border-color: #f5c2c2;
}
.guest-card-body {
    padding: 14px 16px 16px;
}
.guest-head {
    display: flex;
    align-items: center;
    gap: 10px;
}
.avatar {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 1.05rem;
    font-weight: 700;
    color: #344054;
    background: #f2f4f7;
}
.accent-today .avatar {
    color: #8a5a00;
    background: #f7e2b3;
}
.accent-expired .avatar {
    color: #a3251c;
    background: #f5c8c4;
}
.guest-head-text {
    min-width: 0;
}
.guest-name {
    font-size: 0.95rem;
    font-weight: 700;
    color: #101828;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.accent-today .guest-name {
    color: #8a5a00;
}
.accent-expired .guest-name {
    color: #a3251c;
}
.reservation-no {
    margin-top: 2px;
    font-size: 0.72rem;
    letter-spacing: 0.03em;
    color: #98a2b3;
}
.rooms-line {
    display: flex;
    align-items: flex-start;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #eceef1;
    font-size: 0.86rem;
    color: #475467;
}
.rooms-line .fa-bed {
    color: #98a2b3;
    margin-top: 2px;
}
.rooms-text {
    font-weight: 600;
    white-space: normal;
    word-break: break-word;
}
.room-count {
    flex-shrink: 0;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 600;
    color: #475467;
    background: #f2f4f7;
    height: fit-content;
}
.dates-line {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    font-size: 0.78rem;
    color: #667085;
}
.date-item .fa-sign-in-alt,
.date-item .fa-sign-out-alt {
    color: #98a2b3;
}
.empty-state {
    text-align: center;
    color: #98a2b3;
    padding: 60px 0;
    font-size: 0.95rem;
}
</style>
